import { lectureSourceMetadata } from "./lecture-source-metadata";
import { preloadProcessingResultReaders, readProcessingTask, readProcessingTextDocuments } from "./processing-task-reads";
import { getAnonymousUploadClaim } from "./anonymous-upload-claims";
import { LANDING_FILE_PROCESSING_URL, resumeLandingFileProcessing } from "./resumeLandingFileProcessing";
import { getTextFileContentMediumType, hasImportableAnkiTextRows } from "./anki-text-format";
import { type LandingIngestionOptions, type LandingIngestionOutcome } from "./landingHandoffContext";
import {
  ContentMediumType,
  UserData,
  JUNGLE_BACKEND_PROD_DOMAIN,
  DeckObject,
  GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME,
  FileUploadWithDeckData,
  LectureSourceUploadContext,
  MAX_PDF_PAGES_ALLOWED_FOR_FREE,
  MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN,
  SubscriptionStatus,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getContentMediumTypeFromFileNameOrPageUrl, trackEvent } from "../../backend/utils/helper-methods";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../backend/utils/init-firebase";
import {
  cardStore,
  generateCardsStore,
  pageStore,
  useReviewDeckStore,
  legacyReduxStore,
} from "../../zustand/globalStore";
import { waitForUploadBucketToCatchUp } from "../../components/banana-bucket-loader/uploadBucketHandoff";
import { addSentryDiagnosticBreadcrumb, trackError } from "../../utils/analytics/track-error";
import {
  normalizeQuestionGenerationError,
  requestQuestionGeneration,
} from "../../backend/utils/question-generation-observability";
import {
  reportQuestionGenerationFailure,
  reportQuestionGenerationNetworkRetry,
} from "../../backend/utils/question-generation-sentry";
import {
  FileUploadError,
  FileUploadErrorCode,
  FileUploadInputMethod,
  FileUploadStage,
  FileUploadTranslate,
  assertLocalFileIsReadable,
  cloneFileUploadError,
  buildLocalUploadPageUrl,
  createAndWaitForFileProcessing,
  createFileUploadPageExitMonitor,
  createUploadAttemptId,
  getFileProcessingErrorMessage,
  getFileUploadUserMessage,
  getSafeFileMetadata,
  getEffectivePdfPageCount,
  getPresignedPostBucketName,
  isFileUploadCancellation,
  requestFileProcessingTask,
} from "./file-upload-client";
import {
  reportDocumentUploadAbandonment,
  reportDocumentUploadFailure,
  reportDocumentUploadFileUnreadable,
} from "./document-upload-sentry";
// The S3 upload steps live in s3-file-upload.ts so the marketing site's landing page (jungleai.com) can
// upload without this module, which brings the app's stores and Firebase with it. Re-exported for the
// app's existing imports.
import { getUrlForFileUploadToS3, uploadFileToS3 } from "./s3-file-upload";
import { ensureAnonymousReadSession, getSignedInFirebaseUser } from "../../backend/utils/anonymous-read-session";
export { getUrlForFileUploadToS3, uploadFileToS3 };

// file could be pdf or powerpoint (.pptx)
export const createTaskToGetDataFromFile = async (
  formattedFileName: string,
  user: UserData,
  pageUrl: string,
  uploadedFileS3ObjectKey: string,
  contentMediumType: ContentMediumType,
  requestedTaskId = createUploadAttemptId(),
  cancellationSignal?: AbortSignal,
  onProcessingStageChange?: (stage: FileUploadStage) => void,
  processingPurpose: "new_upload" | "source_reprocessing" = "new_upload",
  lectureSourceContext?: LectureSourceUploadContext
): Promise<any> => {
  const authenticatedUser = getSignedInFirebaseUser();
  const idToken = (await authenticatedUser?.getIdToken(false)) ?? null;
  // Token loading can cross a sign-out/account switch. Both the token owner
  // captured before the await and the current account must own this lecture.
  const hasAuthenticatedToken = Boolean(idToken);
  const isSameLectureAccount = authenticatedUser?.uid === user?.id && getSignedInFirebaseUser()?.uid === user?.id;
  if (lectureSourceContext && (!hasAuthenticatedToken || !isSameLectureAccount)) {
    throw new Error("The lecture upload account changed. Please retry.");
  }
  return createAndWaitForFileProcessing({
    requestedTaskId,
    requestTask: async () => {
      if (lectureSourceContext && getSignedInFirebaseUser()?.uid !== user?.id) {
        throw new Error("The lecture upload account changed. Please retry.");
      }
      const returnedTaskId = await requestFileProcessingTask({
        url: `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/create_task_to_get_file_data`,
        requestedTaskId,
        body: {
          file_name: formattedFileName,
          page_url: pageUrl,
          user_id: user?.id,
          uploaded_file_s3_object_key: uploadedFileS3ObjectKey,
          content_medium_type: contentMediumType,
          file_processing_purpose: processingPurpose,
          ...lectureSourceContext,
        },
        idToken,
        cancellationSignal,
      });
      onProcessingStageChange?.(FileUploadStage.AwaitProcessing);
      return returnedTaskId;
    },
    waitForTask: (taskId, options) =>
      getDataFromFileByTask(taskId, {
        ...options,
        cancellationSignal,
      }),
  });
};
async function fetchCombinedText(fileProcessingStatusId: string, cancellationSignal?: AbortSignal): Promise<string[]> {
  // Result validation is only needed after processing, outside app startup.
  const [{ assembleProcessedFileText }, documents] = await Promise.all([
    import("./processed-file-text"),
    readProcessingTextDocuments(fileProcessingStatusId, cancellationSignal),
  ]);
  return assembleProcessedFileText(documents);
}

// file could be pdf or powerpoint (.pptx)
export const getDataFromFileByTask = async (
  taskId: string,
  options?: { allowMissingDocumentGracePeriod: boolean; cancellationSignal?: AbortSignal }
): Promise<any> => {
  const { waitForFileProcessingResult } = await import("./processing-task-wait");
  // The processing status is read through Firestore, which needs a fresh session; a
  // signed-out visitor's upload is watched through an anonymous one.
  if (!getSignedInFirebaseUser()) await ensureAnonymousReadSession();
  return waitForFileProcessingResult({
    taskId,
    allowMissingDocumentGracePeriod: options?.allowMissingDocumentGracePeriod,
    cancellationSignal: options?.cancellationSignal,
    subscribeToTask: (processingTaskId, onData, onError) => {
      const processingStatusDocumentRef = doc(
        db,
        GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME,
        processingTaskId
      );
      return onSnapshot(
        processingStatusDocumentRef,
        (processingSnapshot) => onData(processingSnapshot.exists() ? processingSnapshot.data() : null),
        onError
      );
    },
    fetchTaskSnapshot: readProcessingTask,
    onTaskSnapshotRecovery: (status) => {
      addSentryDiagnosticBreadcrumb({
        category: "document-upload",
        message: "Document processing status recovered by direct read",
        data: {
          taskId,
          status,
          recoveryTransport: "firestore-lite-rest",
        },
      });
    },
    fetchCombinedText: (processingTaskId) => fetchCombinedText(processingTaskId, options?.cancellationSignal),
  });
};

/** The Anki importer must run before the generation screen (which deliberately skips AI generation). */
async function importAnkiDeck(
  newGeneratedDeckData: DeckObject,
  user: UserData,
  generationAttemptId = createUploadAttemptId()
): Promise<DeckObject> {
  newGeneratedDeckData.generation_metadata.question_generation_attempt_id = generationAttemptId;
  const { generatedDeckId, orchestrationModalFunctionCallId } = await requestQuestionGeneration({
    idToken: (await getSignedInFirebaseUser()?.getIdToken(false)) ?? null,
    url: `${JUNGLE_BACKEND_PROD_DOMAIN}/generate_content/run_generations_for_anki_txt`,
    body: {
      generation_attempt_id: generationAttemptId,
      page_text: newGeneratedDeckData.generation_metadata?.page_text?.trim(),
      page_text_sentences_array: newGeneratedDeckData.generation_metadata?.page_text_sentences_array,
      page_url: newGeneratedDeckData.generation_metadata?.page_url,
      page_title: newGeneratedDeckData.generation_metadata?.page_title,
      content_medium_type: ContentMediumType.AnkiTxt,
      landing_handoff_context: newGeneratedDeckData.generation_metadata?.landing_handoff_context,
      uploaded_file_s3_bucket_name: newGeneratedDeckData.generation_metadata?.uploaded_file_s3_bucket_name,
      uploaded_file_s3_object_key: newGeneratedDeckData.generation_metadata?.uploaded_file_s3_object_key,
      get_data_from_file_processing_status_doc_id:
        newGeneratedDeckData.generation_metadata?.get_data_from_file_processing_status_doc_id,
      user_id: user ? user.id : null,
      platform: pageStore.getState().isRunningAsMobileApp ? "Mobile App" : "Web",
      pdf_file_name: newGeneratedDeckData.generation_metadata?.pdf_file_name,
    },
    onNetworkRetry: ({ error, failedAttemptNumber, maximumAttempts }) => {
      reportQuestionGenerationNetworkRetry({
        error,
        attemptId: generationAttemptId,
        deck: newGeneratedDeckData,
        user,
        failedAttemptNumber,
        maximumAttempts,
        sourceDeckId: newGeneratedDeckData.id,
      });
    },
  });
  newGeneratedDeckData.generation_metadata.orchestration_modal_function_call_id =
    orchestrationModalFunctionCallId || undefined;

  return { ...newGeneratedDeckData, id: generatedDeckId };
}

export const getTextFromUploadedFileUrlInQueryParams = async (
  fileTaskId: string,
  fileName: string,
  setErrorMessage: any,
  user: UserData,
  setSelectedOcclusionImagesWithBoxPositions: any,
  navigate: any,
  setIsFileGettingDataUploaded: any,
  setCurrentReviewDeck: (deck: DeckObject) => void,
  setCurrentContentMediumType: (contentMediumType: ContentMediumType) => void,
  setPercentage?: any | null,
  translate?: FileUploadTranslate,
  options: LandingIngestionOptions = {}
): Promise<LandingIngestionOutcome> => {
  setErrorMessage(null);
  let shouldAutoGenerateQuestions = false;
  const haveCardsBeenGeneratedBefore = localStorage.getItem("haveCardsBeenGeneratedBefore");
  if ((!user && !haveCardsBeenGeneratedBefore) || user?.number_of_sets_of_cards_generated === 0) {
    shouldAutoGenerateQuestions = true;
  }

  if (options.restoreToReview) shouldAutoGenerateQuestions = false;

  let pageUrl = null;
  try {
    let pdfNumPages = 0;
    let pdfPagesTextArray = [];
    let pdfImagesObjectListDocId = null;
    let videoOrAudioTimeLengthInSeconds = 0;

    // Landing already transferred the bytes. Resume at the shared processing
    // boundary so the app says it is reading the source, not uploading it again.
    if (setPercentage) setPercentage(60);

    const contentMediumType: ContentMediumType = Object.values(ContentMediumType).includes(options.sourceContentMediumType as ContentMediumType)
      ? options.sourceContentMediumType as ContentMediumType
      : getContentMediumTypeFromFileNameOrPageUrl(fileName);
    setCurrentContentMediumType(contentMediumType);

    let fileData: any = await resumeLandingFileProcessing({
      taskId: fileTaskId,
      waitForTask: taskId => getDataFromFileByTask(taskId, {
        allowMissingDocumentGracePeriod: true,
        cancellationSignal: options.cancellationSignal,
      }),
      getIdToken: async () => (await getSignedInFirebaseUser()?.getIdToken(false)) ?? null,
      cancellationSignal: options.cancellationSignal,
      onRecovered: () => addSentryDiagnosticBreadcrumb({
        category: "document-upload", message: "Landing processing task recovered without uploading again",
        data: { taskId: fileTaskId, recoveryTransport: "idempotent-task-request" },
      }),
    });
    if (options.cancellationSignal?.aborted) return "cancelled";
    let status = fileData.status;
    if (status === "error") {
      const queryTaskError = new FileUploadError({
        message: typeof fileData?.message === "string" ? fileData.message : "Document processing failed",
        stage: FileUploadStage.AwaitProcessing,
        code: FileUploadErrorCode.ProcessingFailed,
        retryable: false,
      });
      trackError(queryTaskError, {
        scope: "document-upload-query-task",
        userId: user?.id,
        tags: {
          file_upload_stage: queryTaskError.stage,
          file_upload_error_code: queryTaskError.code,
          ingestion_attempt_id: fileTaskId,
          file_processing_task_id: fileTaskId,
          input_method: "query-parameter",
          file_processing_error_code: String(fileData?.file_processing_error_code || "unknown"),
          file_processing_purpose: String(fileData?.file_processing_purpose || "unknown"),
        },
        extra: {
          taskId: fileTaskId,
          fileExtension: fileName.split(".").pop()?.toLowerCase() || "unknown",
          sentryOrigin: "frontend",
          isOnline: navigator.onLine,
        },
      });
      setErrorMessage(getFileProcessingErrorMessage(fileData, fileName, translate));
      setIsFileGettingDataUploaded(false);
      return "failure";
    }

    // Current tasks own the canonical (possibly converted) key. Older tasks may
    // omit it; only the exact handoff tuple may supply the original source key.
    const uploadedFileS3ObjectKey = fileData?.uploaded_file_s3_object_key
      || getAnonymousUploadClaim(fileTaskId, LANDING_FILE_PROCESSING_URL)?.body.uploaded_file_s3_object_key;
    // Parsed Anki text is already self-contained; every other source needs retrievable bytes.
    const isRequiredSourceUnavailable = !uploadedFileS3ObjectKey && contentMediumType !== ContentMediumType.AnkiTxt;
    if (isRequiredSourceUnavailable) {
      throw new FileUploadError({
        message: "The saved upload has no recoverable storage identity",
        stage: FileUploadStage.RetrieveSource, code: FileUploadErrorCode.SourceUnavailable, retryable: false,
      });
    }
    pageUrl = buildLocalUploadPageUrl(fileTaskId, uploadedFileS3ObjectKey || fileName);
    pdfPagesTextArray = fileData?.pdfPagesTextArray || [];
    pdfNumPages = getEffectivePdfPageCount(fileData?.pdfNumPages, pdfPagesTextArray);
    pdfImagesObjectListDocId = fileData.pdfImagesDocumentId;
    videoOrAudioTimeLengthInSeconds = fileData?.video_or_audio_time_length_in_seconds
      ? fileData?.video_or_audio_time_length_in_seconds
      : null;

    if (setPercentage) setPercentage(100);

    // Calculate max allowed pages based on user subscription
    let maxPdfPagesAllowed = MAX_PDF_PAGES_ALLOWED_FOR_FREE;
    if (user?.subscription_information?.subscription_status === SubscriptionStatus.ACTIVE) {
      maxPdfPagesAllowed = MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN;
    }
    // Set the default ending page to the minimum of pdf pages and allowed limit
    const defaultEndingPage = Math.min(pdfNumPages, maxPdfPagesAllowed);

    const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, "");
    const newDeckData: DeckObject = {
      id: null,
      date_created: new Date(),
      generation_metadata: {
        landing_handoff_context: options.context,
        ...lectureSourceMetadata(fileData?.lecture_source),
        page_text: pdfPagesTextArray.join(" "),
        page_text_sentences_array: pdfPagesTextArray,
        page_title: fileNameWithoutExtension,
        content_medium_type: contentMediumType || ContentMediumType.PDF,
        pdf_url_for_file_upload: true,
        pdf_file_name: fileName,
        pdf_num_pages: pdfNumPages,
        pdf_ending_page: defaultEndingPage,
        pdf_starting_page: 1,
        pdf_pages_text_array: pdfPagesTextArray,
        pdf_images_object_list_doc_id: pdfImagesObjectListDocId,
        video_or_audio_num_minutes: videoOrAudioTimeLengthInSeconds
          ? Math.ceil(videoOrAudioTimeLengthInSeconds / 60)
          : null,
        video_or_audio_ending_minute: videoOrAudioTimeLengthInSeconds
          ? Math.ceil(videoOrAudioTimeLengthInSeconds / 60)
          : null,
        video_or_audio_starting_minute: 0,
        pdf_images_object_list: null,
        page_text_hash: null,
        page_url: pageUrl,
        uploaded_file_s3_object_key: uploadedFileS3ObjectKey,
        get_data_from_file_processing_status_doc_id: fileTaskId,
      },
    };

    if (contentMediumType === ContentMediumType.Image) {
      newDeckData.generation_metadata.pdf_images_object_list = [{ imageUrl: pageUrl, fileName: fileName }];
    }

    let preparedDeck = newDeckData;
    if (contentMediumType === ContentMediumType.AnkiTxt) {
      if (!hasImportableAnkiTextRows(newDeckData.generation_metadata.page_text || "")) {
        setErrorMessage("This is not a valid Anki txt file. Please try again.");
        setIsFileGettingDataUploaded(false);
        return "failure";
      }
      preparedDeck = await importAnkiDeck(newDeckData, user, options.generationAttemptId || fileTaskId);
    }
    if (options.cancellationSignal?.aborted || (options.onReady && !options.onReady({ deckId: preparedDeck.id || undefined, sourceUrl: pageUrl }))) return "cancelled";
    if (!options.restoreToReview) trackEvent("Document Upload Attempt Completed", {
      outcome: "success",
      inputMethod: "landing-handoff",
      contentMediumType,
      fileExtension: fileName.includes(".") ? fileName.split(".").pop()?.toLowerCase() : "unknown",
      ...options.context,
    }, user, null);
    let sourceToGeneration: FileUploadWithDeckData[] = [
      {
        file: null,
        associatedDeck: preparedDeck,
        isBeingRead: false,
        hasUploadError: false,
        uploadErrorMessage: null,
        uploadedFileIndex: 0,
      },
    ];

    setCurrentReviewDeck(preparedDeck);
    useReviewDeckStore.getState().setCurrentInputIndexInDocumentsAndUrlsArray(0);
    useReviewDeckStore.getState().setDocumentsAndUrlsForCardGenerationArray(sourceToGeneration);
    generateCardsStore.getState().setShouldShowSelectImagesForOcclusionView(false);
    setSelectedOcclusionImagesWithBoxPositions(null);
    legacyReduxStore.getState().setIsRegeneratingQuestions(false);
    setSelectedOcclusionImagesWithBoxPositions(null);
    navigate(shouldAutoGenerateQuestions ? "/generate-questions/?autoGenerate=true" : "/generate-questions");
    return "success";
  } catch (error) {
    if (options.cancellationSignal?.aborted || isFileUploadCancellation(error)) return "cancelled";
    trackError(error, {
      scope: "document-upload-query-task",
      userId: user?.id,
      tags: {
        file_upload_stage: error instanceof FileUploadError ? error.stage : FileUploadStage.AwaitProcessing,
        ingestion_attempt_id: fileTaskId,
        file_processing_task_id: fileTaskId,
      },
      extra: { taskId: fileTaskId, inputMethod: "query-parameter" },
    });
    setErrorMessage(getFileUploadUserMessage(error, navigator.onLine, translate));
    setIsFileGettingDataUploaded(false);
    return "failure";
  }
};

function normalizeFileUploadError(error: unknown, stage: FileUploadStage): FileUploadError {
  if (error instanceof FileUploadError) {
    return error;
  }
  return new FileUploadError({
    message: error instanceof Error ? error.message : "Unexpected document upload failure",
    stage,
    code: FileUploadErrorCode.ProcessingFailed,
    retryable: true,
    cause: error,
  });
}

const UPLOAD_BYTE_PROGRESS_WRITE_INTERVAL_MS = 250;

export const readFile = async (
  file: File,
  setErrorMessage: any,
  user: UserData,
  contentMediumType: ContentMediumType | null,
  setIsFileGettingDataUploaded: any,
  setPercentage?: any | null,
  setCurrentReviewDeck?: (deck: DeckObject) => void,
  inputMethod: FileUploadInputMethod = "unknown",
  translate?: FileUploadTranslate,
  cancellationSignal?: AbortSignal,
  onStorageUploadCompleted?: () => void,
  lectureSourceContext?: LectureSourceUploadContext
) => {
  setErrorMessage(null);
  let pageUrl = null;
  const uploadAttemptId = createUploadAttemptId();
  let uploadedFileBucketName: string | null = null;
  let uploadedFileS3ObjectKey: string | null = null;
  let fileProcessingTaskId: string | null = uploadAttemptId;
  let fileProcessingModalFunctionCallId: string | null = null;
  let currentUploadStage = FileUploadStage.ValidateInput;
  let currentUploadProgressPercentage = 0;
  // Later processing failures are built after storage succeeded, so they must
  // inherit whether the stored object came from an in-memory copy of the file.
  let storedObjectCameFromInMemoryCopy = false;
  const uploadStartedAtMs = Date.now();
  let storageFileSize = 0;
  let storageUploadStartedAtMs = 0;
  let displayedUploadBytes = 0;
  // The transfer reports progress many times a second, and every store write re-renders the upload
  // screen and the page under it, which makes its animation stutter on phones. The bucket moves in
  // 2.5% steps and the byte line is read at a glance, so whole percents and a few byte updates a
  // second carry the same real progress.
  let lastWrittenTransferPercentage = -1;
  let lastByteProgressWriteAtMs = 0;
  const updateUploadProgress = (progressPercentage: number) => {
    currentUploadProgressPercentage = progressPercentage;
    if (setPercentage) setPercentage(progressPercentage);
  };
  // Only a caller that shows upload progress gets the file's byte counts too. They belong to this
  // attempt: cleared once its bytes are sent or it fails, never touching a newer attempt's counts, so
  // a later URL or YouTube import never shows them.
  const updateUploadByteProgress = (progressPercent: number, storageUploadStartedAtMs: number) => {
    if (!setPercentage || cancellationSignal?.aborted) return;
    if (!Number.isFinite(progressPercent)) return;
    // A recovery transport sends the same prepared body again. Keep the display
    // monotonic within this attempt, without changing raw transport diagnostics.
    displayedUploadBytes = Math.max(displayedUploadBytes,
      Math.round(storageFileSize * Math.min(100, Math.max(0, progressPercent)) / 100));
    cardStore.getState().setUploadByteProgress({
      attemptId: uploadAttemptId,
      fileName: file.name,
      loadedBytes: displayedUploadBytes,
      totalBytes: storageFileSize,
      startedAtMs: storageUploadStartedAtMs,
      updatedAtMs: Date.now(),
    });
  };
  const clearUploadByteProgress = () => {
    if (!setPercentage) return;
    const { uploadByteProgress, setUploadByteProgress } = cardStore.getState();
    if (uploadByteProgress?.attemptId === uploadAttemptId) setUploadByteProgress(null);
  };
  const reportAbandonedUpload = () => {
    reportDocumentUploadAbandonment({
      attemptId: uploadAttemptId,
      inputMethod,
      file,
      contentMediumType,
      user,
      stage: currentUploadStage,
      progressPercentage: currentUploadProgressPercentage,
      elapsedMs: Date.now() - uploadStartedAtMs,
      stableUploadedFileIdentifiers: {
        bucketName: uploadedFileBucketName,
        objectKey: uploadedFileS3ObjectKey,
        taskId: fileProcessingTaskId,
        modalFunctionCallId: fileProcessingModalFunctionCallId,
      },
    });
  };
  const pageExitMonitor = createFileUploadPageExitMonitor({
    onPageExit: reportAbandonedUpload,
    cancellationSignal,
  });
  try {
    addSentryDiagnosticBreadcrumb({
      category: "document-upload",
      message: "File ingestion started",
      data: {
        attemptId: uploadAttemptId,
        inputMethod,
        contentMediumType,
        ...getSafeFileMetadata(file),
      },
    });
    updateUploadProgress(5);
    // 0-byte local guard: show inline error and abort without any backend calls
    if (file && typeof file.size === "number" && file.size === 0) {
      const emptyFileError = new FileUploadError({
        message: "The selected file is empty",
        stage: FileUploadStage.ValidateInput,
        code: FileUploadErrorCode.EmptyFile,
        retryable: false,
      });
      setErrorMessage(getFileUploadUserMessage(emptyFileError, navigator.onLine, translate));
      reportDocumentUploadFailure({
        error: emptyFileError,
        attemptId: uploadAttemptId,
        inputMethod,
        file,
        contentMediumType,
        user,
      });
      updateUploadProgress(0);
      setIsFileGettingDataUploaded(false);
      return;
    }
    currentUploadStage = FileUploadStage.ReadLocalFile;
    // Android WebViews can hand the page a File whose content:// source is no
    // longer readable. Every request carrying that File then fails as a fast
    // network error, so prove the bytes are readable before spending a presign
    // and the whole storage transport ladder on it.
    await assertLocalFileIsReadable({ file, cancellationSignal });
    // A .txt file is either an Anki export or notes to generate questions from; only its text says which.
    if ((contentMediumType ?? getContentMediumTypeFromFileNameOrPageUrl(file.name, file.type)) === ContentMediumType.AnkiTxt) {
      contentMediumType = await getTextFileContentMediumType(file);
    }
    // Cache result readers before upload side effects. A deploy during a long
    // upload must not leave its completed result waiting on a vanished chunk.
    currentUploadStage = FileUploadStage.ValidateInput;
    await preloadProcessingResultReaders();
    let formattedFileName = file.name.replace(/\s/g, "+");
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    // when image is svg, convert to png for better processing of transparent background
    if (fileExtension === "svg") {
      // change the file name to .png
      formattedFileName = formattedFileName.replace(/\.svg$/i, ".png");
    }
    currentUploadStage = FileUploadStage.RequestUploadUrl;
    const urlForFileUpload = await getUrlForFileUploadToS3(
      formattedFileName,
      user,
      contentMediumType,
      uploadAttemptId,
      cancellationSignal
    );
    uploadedFileBucketName = getPresignedPostBucketName(urlForFileUpload);
    uploadedFileS3ObjectKey = urlForFileUpload.fields.key;
    addSentryDiagnosticBreadcrumb({
      category: "document-upload",
      message: "Presigned upload request completed",
      data: {
        attemptId: uploadAttemptId,
        uploadedFileBucketName,
        uploadedFileObjectKey: uploadedFileS3ObjectKey,
      },
    });
    updateUploadProgress(10);
    currentUploadStage = FileUploadStage.UploadToStorage;
    await uploadFileToS3(file, urlForFileUpload, user, contentMediumType, uploadAttemptId, {
      cancellationSignal,
      preferProgressAwareTransport: true,
      inputMethod,
      onUploadFilePrepared: totalBytes => {
        storageFileSize = totalBytes;
        storageUploadStartedAtMs = Date.now();
        updateUploadByteProgress(0, storageUploadStartedAtMs);
      },
      onUploadProgress: ({ progressPercent }) => {
        const transferPercentage = Math.floor(10 + progressPercent * 0.45);
        if (transferPercentage !== lastWrittenTransferPercentage) {
          lastWrittenTransferPercentage = transferPercentage;
          updateUploadProgress(transferPercentage);
        }
        const nowMs = Date.now();
        if (progressPercent >= 100 || nowMs - lastByteProgressWriteAtMs >= UPLOAD_BYTE_PROGRESS_WRITE_INTERVAL_MS) {
          lastByteProgressWriteAtMs = nowMs;
          updateUploadByteProgress(progressPercent, storageUploadStartedAtMs);
        }
      },
      onStorageTransportRecovery: ({ bodyMaterialized }) => {
        storedObjectCameFromInMemoryCopy = bodyMaterialized;
      },
    });
    onStorageUploadCompleted?.();
    clearUploadByteProgress();
    addSentryDiagnosticBreadcrumb({
      category: "document-upload",
      message: "Storage upload completed",
      data: { attemptId: uploadAttemptId },
    });
    updateUploadProgress(60);

    let pdfNumPages = 0;
    let pdfPagesTextArray = [];
    let pdfImagesObjectListDocId = null;
    let videoOrAudioTimeLengthInSeconds = 0;
    let getDataFromFileProcessingStatusDocId = null;
    let uploaded_file_s3_object_key = urlForFileUpload.fields.key;
    const storageUploadBaseUrl = urlForFileUpload.url.endsWith("/") ? urlForFileUpload.url : `${urlForFileUpload.url}/`;
    const storageUploadUrl = new URL(uploaded_file_s3_object_key, storageUploadBaseUrl).toString();

    if (!contentMediumType) {
      contentMediumType = getContentMediumTypeFromFileNameOrPageUrl(file.name, file.type);
    }

    currentUploadStage = FileUploadStage.CreateProcessingTask;
    const fileData = await createTaskToGetDataFromFile(
      formattedFileName,
      user,
      storageUploadUrl,
      uploaded_file_s3_object_key,
      contentMediumType,
      uploadAttemptId,
      cancellationSignal,
      (stage) => {
        currentUploadStage = stage;
      },
      "new_upload",
      lectureSourceContext
    );
    addSentryDiagnosticBreadcrumb({
      category: "document-upload",
      message: "Processing task settled",
      data: { attemptId: uploadAttemptId, status: fileData?.status || "unknown" },
    });
    updateUploadProgress(75);

    // If backend attached quick PPT/PPTX or PDF health info, surface a single standardized banner.
    try {
      const pptHealth = (fileData as any)?.pptHealth;
      if (pptHealth && pptHealth.corruptedSuspected === true) {
        setErrorMessage(
          getFileProcessingErrorMessage(
            { status: "error", file_processing_error_code: "file_corrupted" },
            file.name,
            translate
          )
        );
      }

      const pdfHealth = (fileData as any)?.pdfHealth;
      if (pdfHealth) {
        if (pdfHealth.encrypted === true) {
          setErrorMessage(
            getFileProcessingErrorMessage(
              { status: "error", file_processing_error_code: "pdf_encrypted" },
              file.name,
              translate
            )
          );
        }

        if (pdfHealth.corruptedSuspected === true) {
          setErrorMessage(
            getFileProcessingErrorMessage(
              { status: "error", file_processing_error_code: "file_corrupted" },
              file.name,
              translate
            )
          );
        }
      }
    } catch (e) {
      // non-blocking: if anything goes wrong, continue normally
    }

    pdfPagesTextArray = fileData?.pdfPagesTextArray || [];
    pdfNumPages = getEffectivePdfPageCount(fileData?.pdfNumPages, pdfPagesTextArray);
    pdfImagesObjectListDocId = fileData?.pdfImagesDocumentId;
    videoOrAudioTimeLengthInSeconds = fileData?.video_or_audio_time_length_in_seconds || 0;
    uploaded_file_s3_object_key = fileData?.uploaded_file_s3_object_key || uploaded_file_s3_object_key;
    uploadedFileS3ObjectKey = uploaded_file_s3_object_key;
    getDataFromFileProcessingStatusDocId = fileData?.getDataFromFileProcessingStatusDocId || null;
    fileProcessingTaskId = getDataFromFileProcessingStatusDocId || uploadAttemptId;
    fileProcessingModalFunctionCallId = fileData?.modal_function_call_id || null;
    pageUrl = buildLocalUploadPageUrl(
      getDataFromFileProcessingStatusDocId || uploadAttemptId,
      uploaded_file_s3_object_key || formattedFileName
    );
    if (fileData.status === "error") {
      const processingError = new FileUploadError({
        message: typeof fileData?.message === "string" ? fileData.message : "Document processing failed",
        stage: FileUploadStage.AwaitProcessing,
        code: FileUploadErrorCode.ProcessingFailed,
        retryable: false,
        bodyMaterialized: storedObjectCameFromInMemoryCopy,
      });
      setErrorMessage(getFileProcessingErrorMessage(fileData, file.name, translate));
      reportDocumentUploadFailure({
        error: processingError,
        attemptId: uploadAttemptId,
        inputMethod,
        file,
        contentMediumType,
        user,
        stableUploadedFileIdentifiers: {
          bucketName: uploadedFileBucketName,
          objectKey: uploaded_file_s3_object_key,
          taskId: fileProcessingTaskId,
          modalFunctionCallId: fileProcessingModalFunctionCallId,
        },
        processingErrorCode:
          typeof fileData?.file_processing_error_code === "string" ? fileData.file_processing_error_code : null,
        processingErrorMessage: typeof fileData?.message === "string" ? fileData.message : null,
      });
      setIsFileGettingDataUploaded(false);
      return;
    }

    pageExitMonitor.markSettled();
    updateUploadProgress(100);

    // Identify if this is a valid Anki export. Ordinary text files are not
    // accepted by the downstream Anki parser.
    if (contentMediumType === ContentMediumType.AnkiTxt) {
      const pageText = pdfPagesTextArray[0] || "";
      if (!hasImportableAnkiTextRows(pageText)) {
        const invalidAnkiFileError = new FileUploadError({
          message: "The text file is not a valid Anki export",
          stage: FileUploadStage.AwaitProcessing,
          code: FileUploadErrorCode.ProcessingFailed,
          retryable: false,
        });
        reportDocumentUploadFailure({
          error: invalidAnkiFileError,
          attemptId: uploadAttemptId,
          inputMethod,
          file,
          contentMediumType,
          user,
          stableUploadedFileIdentifiers: {
            bucketName: uploadedFileBucketName,
            objectKey: uploaded_file_s3_object_key,
            taskId: fileProcessingTaskId,
            modalFunctionCallId: fileProcessingModalFunctionCallId,
          },
        });
        setErrorMessage("This is not a valid Anki txt file. Please try again.");
        setIsFileGettingDataUploaded(false);
        return;
      }
    }

    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");

    // Calculate max allowed pages based on user subscription
    let maxPdfPagesAllowed = MAX_PDF_PAGES_ALLOWED_FOR_FREE;
    if (user?.subscription_information?.subscription_status === SubscriptionStatus.ACTIVE) {
      maxPdfPagesAllowed = MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN;
    }
    // Set the default ending page to the minimum of pdf pages and allowed limit
    const defaultEndingPage = Math.min(pdfNumPages, maxPdfPagesAllowed);

    // Fix page title not working because it removes the period
    const newGeneratedDeckData: DeckObject = {
      id: null,
      date_created: new Date(),
      generation_metadata: {
        ...lectureSourceMetadata(fileData?.lecture_source),
        page_text: pdfPagesTextArray.join(" "),
        page_text_sentences_array: pdfPagesTextArray,
        page_title: fileNameWithoutExtension,
        content_medium_type: contentMediumType,
        // This field is only used as a boolean upload-origin marker. Do not
        // retain the presigned POST policy and signature in deck state.
        pdf_url_for_file_upload: true,
        pdf_file_name: formattedFileName,
        uploaded_file_s3_object_key: uploaded_file_s3_object_key,
        uploaded_file_s3_bucket_name: uploadedFileBucketName || undefined,
        pdf_num_pages: pdfNumPages,
        pdf_ending_page: defaultEndingPage,
        pdf_starting_page: 1,
        pdf_pages_text_array: pdfPagesTextArray,
        pdf_images_object_list_doc_id: pdfImagesObjectListDocId,
        video_or_audio_num_minutes: Math.ceil(videoOrAudioTimeLengthInSeconds / 60),
        video_or_audio_starting_minute: 0,
        video_or_audio_ending_minute: Math.ceil(videoOrAudioTimeLengthInSeconds / 60),
        pdf_images_object_list: null,
        page_text_hash: null,
        page_url: pageUrl,
        get_data_from_file_processing_status_doc_id: getDataFromFileProcessingStatusDocId,
      },
    };

    if (newGeneratedDeckData?.generation_metadata?.content_medium_type === ContentMediumType.Image) {
      newGeneratedDeckData.generation_metadata.pdf_images_object_list = [
        {
          // Renderers resolve this marker through the owner-authorized task
          // endpoint. Never persist the temporary signed URL in deck state.
          imageUrl: pageUrl,
          fileName: formattedFileName,
        },
      ];
    }

    // A fast upload reports 100% and moves on in the same moment; give the loader's bucket a beat to
    // draw that last real progress first, so the pile is never cut off part way.
    await waitForUploadBucketToCatchUp();
    if (cancellationSignal?.aborted) {
      setIsFileGettingDataUploaded(false);
      return;
    }
    trackEvent(
      "Document Upload Attempt Completed",
      {
        outcome: "success",
        attemptId: uploadAttemptId,
        inputMethod,
        contentMediumType,
        fileExtension: file.name.split(".").pop()?.toLowerCase() || "",
        fileMimeType: file.type || "unknown",
        fileSizeBytes: file.size,
      },
      user,
      null
    );
    if (contentMediumType === ContentMediumType.AnkiTxt) {
      const generationAttemptId = createUploadAttemptId();
      try {
        setCurrentReviewDeck?.(await importAnkiDeck(newGeneratedDeckData, user, generationAttemptId));
        return;
      } catch (error) {
        const ankiGenerationError = normalizeQuestionGenerationError(error);
        reportQuestionGenerationFailure({
          error: ankiGenerationError,
          attemptId: generationAttemptId,
          deck: newGeneratedDeckData,
          user,
          sourceDeckId: newGeneratedDeckData.id,
        });
        setErrorMessage(
          translate?.(
            "question_generation_error",
            "we couldn't generate questions. try again."
          ) || "we uploaded the file, but couldn’t create questions from it. please try generating again."
        );
        return;
      } finally {
        // Recovery must stay busy until the imported deck is handed back.
        setIsFileGettingDataUploaded(false);
      }
    }
    // else {
    //   navigate(shouldAutoGenerateQuestions ? "/generate-questions/?autoGenerate=true" : "/generate-questions");
    // }
    setCurrentReviewDeck?.(newGeneratedDeckData);
    setIsFileGettingDataUploaded(false);
  } catch (error) {
    clearUploadByteProgress();
    const normalizedFileUploadError = normalizeFileUploadError(error, currentUploadStage);
    let fileUploadError = normalizedFileUploadError;
    if (storedObjectCameFromInMemoryCopy && !normalizedFileUploadError.bodyMaterialized) {
      fileUploadError = cloneFileUploadError(normalizedFileUploadError, { bodyMaterialized: true });
      // Keep the failing request's stack so Sentry still points at, and groups by, the real failure.
      fileUploadError.stack = normalizedFileUploadError.stack;
    }
    if (isFileUploadCancellation(fileUploadError)) {
      // The batch owner resets progress before aborting. A cancelled attempt
      // may settle after its replacement has started, so it must not write to
      // the shared progress store here.
      setIsFileGettingDataUploaded(false);
      return;
    }
    if (fileUploadError.code === FileUploadErrorCode.FileUnreadable) {
      // The browser never reached S3 or Jungle's backend; count this apart
      // from real transport failures so the picker problem stays visible.
      reportDocumentUploadFileUnreadable({
        error: fileUploadError,
        attemptId: uploadAttemptId,
        inputMethod,
        file,
        contentMediumType,
        user,
      });
    } else {
      reportDocumentUploadFailure({
        error: fileUploadError,
        attemptId: uploadAttemptId,
        inputMethod,
        file,
        contentMediumType,
        user,
        stableUploadedFileIdentifiers: {
          bucketName: uploadedFileBucketName,
          objectKey: uploadedFileS3ObjectKey,
          taskId: fileProcessingTaskId,
          modalFunctionCallId: fileProcessingModalFunctionCallId,
        },
      });
    }
    setErrorMessage(getFileUploadUserMessage(fileUploadError, navigator.onLine, translate));
    setIsFileGettingDataUploaded(false);
  } finally {
    pageExitMonitor.dispose();
  }
};
